import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Dropdown, Form } from 'react-bootstrap'
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons'
import { useNavigate, useSearchParams, Link, useParams } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'

import classNames from 'classnames'
import moment from 'moment'

import 'moment/locale/ru'
import gear from '../../assets/icons/gear.png'
import ListNoItems from '../../components/_atoms/ListNoItems/ListNoItems'
import ListPagination from '../../components/_atoms/ListPagination/ListPagination'
import SalesButton from '../../components/_atoms/SalesButton/SalesButton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import ModalCustomizableColumns from '../../components/_molecules/ModalCustomizableColumns/ModalCustomizableColumns'
import { ModalPartsBulkApply } from '../../components/_molecules/ModalPartsBulkActions/ModalPartsBulkApply'
import { ModalPartsBulkConfirm } from '../../components/_molecules/ModalPartsBulkActions/ModalPartsBulkConfirm'
import ModalViewPart from '../../components/_molecules/ModalViewPart/ModalViewPart'
import NavigationButtons from '../../components/_molecules/NavigationButtons/NavigationButtons'
import SearchComponent from '../../components/_molecules/SearchComponent/SearchComponent'
import { allCustomizableColumnsSold, defaultCustomizableColumnsSold } from '../../constants/defaultCustomizableColumns'
import { FilterSelect } from '../../constants/filterSelect'
import { pages } from '../../constants/parts/navigationButtonsPages'
import { colors, fontSizes, fontWeights } from '../../constants/salesStyles'
import useResetFiltersOnUnmount from '../../hooks/partList/useResetFiltersOnUnmount'
import DatahubApiService from '../../modules/api/DatahubApiService'
import getCarById from '../../modules/redux/createUpdateForm/getCarById'
import getDocumentsSelectSearch from '../../modules/redux/documentList/GetDocumentsSelectSearch'
import loadPartList from '../../modules/redux/partList/LoadPartList'
import setPartSoldStatus from '../../modules/redux/partList/SetPartSoldStatus'
import getSummaryByUserId from '../../modules/redux/sales/getSummaryByUserId'
import warehousesSelectSearch from '../../modules/redux/warehouseList/WarehousesSelectSearch'
import { useDispatch } from '../../modules/store/customDispatch'
import { useSelector } from '../../modules/store/customSelector'
import { Column } from '../../modules/store/partList/partListReducer'
import { storeDispatchTypes } from '../../modules/store/storeDispatchTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import { EDocumentType } from '../../types/document/documentTypes'
import {
    Marketplaces,
    MarketplacesKeys,
    Part,
    PartListPageParams,
    PartListSearchParams,
} from '../../types/part/partTypes'
import { getCurrencySymbol } from '../../utils/getCurrencySymbol'

import styles from './styles.module.scss'
import TableSoldPartList from './TableSoldPartList'

const SoldPartList = () => {
    const params = useParams<PartListPageParams>()
    const dispatch = useDispatch()
    const [period, setPeriod] = useState<FilterSelect>(FilterSelect.ALL)
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [counts, setCounts] = useState<Record<string, number>>()
    const [isPartModalOpen, setIsPartModalOpen] = useState(false)
    const navigate = useNavigate()
    const [pagesParts, setPagesParts] = useState(pages)

    const {
        currentPart,
        isLoading: { partList: isLoading },
        partList: { data: parts, dataCount: itemsCount, total: itemsTotal, draftCount, archivedCount, soldCount },
        filters: {
            car: carFilter,
            modelId,
            markId,
            categoryId,
            partId,
            partNumber,
            partName,
            priceRange,
            dateRange,
            page,
            perPage,
            filterVendorCode,
            marketplace,
            document,
            warehouseId: filterWarehouseId,
        },
        sortOrder,
        currentCurrency,
    } = useSelector(state => state.partList)
    const canShowFinanceInfo = useSelector(state => state.userData.perms?.canShowFinanceInfo)

    const { from: priceFrom, to: priceTo } = priceRange || {}
    const { from: dateFrom, to: dateTo } = dateRange || {}
    const { documentId } = document || {}

    const userId = useSelector(state => state.userData.smsAuth.userId)
    const warehousesSS = useSelector(state => state.warehouseList.warehousesSS)
    const [isCustomizableColumnsModalOpen, setIsCustomizableColumnsModalOpen] = useState(false)
    const [customizableColumns, setCustomizableColumns] = useState<Column[]>(() => [...defaultCustomizableColumnsSold])
    const {
        documents,
        loaders: { documents: isLoadingDocuments },
    } = useSelector(state => state.documentList)

    // Фильтрация по параметрам
    const filterParts = useCallback(
        (type: storeDispatchTypes, value: any) =>
            dispatch({
                type,
                value,
            }),
        [dispatch],
    )

    const updateCounts = useCallback(() => {
        if (!isLoading) {
            setCounts({
                itemsTotal,
                draftCount,
                archivedCount,
                soldCount,
            })
        }
    }, [archivedCount, draftCount, isLoading, itemsTotal, soldCount])

    useEffect(() => {
        updateCounts()
    }, [updateCounts])

    const applyFilters = useMemo(
        () => ({
            partName: (value: string | null) =>
                filterParts(storeDispatchTypes.setPartNameTextFilterParts, { partName: value }),
            categoryId: (value: string | null) =>
                filterParts(storeDispatchTypes.setCategoryFilterParts, { filterId: value }),
            markId: (value: string | null) => filterParts(storeDispatchTypes.setMarkFilterParts, { filterId: value }),
            modelId: (value: string | null) => filterParts(storeDispatchTypes.setModelFilterParts, { filterId: value }),
            priceRange: (from: string | null, to: string | null) =>
                filterParts(storeDispatchTypes.setPriceRangeParts, { from, to }),
            partNumber: (value: string | null) =>
                filterParts(storeDispatchTypes.setPartNumberFilterParts, { filterId: value }),
            sortOrder: (field: string, order: string) =>
                filterParts(storeDispatchTypes.setPartListSort, { sortField: field, sortOrder: order }),
            marketplace: (value: string | null) =>
                filterParts(storeDispatchTypes.setPartListParamsMarketplace, { marketplace: value }),
            documentId: (value: string | null) =>
                filterParts(storeDispatchTypes.setDocumentFilterParts, { document: { documentId: value } }),
            dateRange: (from: string | Date | null, to: string | Date | null) =>
                filterParts(storeDispatchTypes.setDateRangeParts, { from, to }),
        }),
        [filterParts],
    )

    const filterPartsByMarketplace = useCallback(
        (marketplace: MarketplacesKeys | null) => {
            applyFilters.marketplace(marketplace)
        },
        [applyFilters],
    )

    const filterPartsByWarehouseId = useCallback(
        (warehouseId: string) => {
            filterParts(storeDispatchTypes.setFilterWarehouseIdParts, { warehouseId })
        },
        [filterParts],
    )

    const handleSelectChangeDocument = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            applyFilters.documentId(e.target.value)
        },
        [applyFilters],
    )

    // Параметры поиска
    const [searchParams, setSearchParams] = useSearchParams()
    const [isSearchParamsLoaded, setIsSearchParamsLoaded] = useState(false)

    useEffect(() => {
        dispatch(getDocumentsSelectSearch(EDocumentType.IncomingInvoice))
        dispatch(warehousesSelectSearch(''))
    }, [dispatch])

    useEffect(() => {
        if (!userId) {
            setCustomizableColumns([...defaultCustomizableColumnsSold])
            return
        }

        try {
            const savedColumnIds = JSON.parse(localStorage.getItem(`soldPartListColumns:${userId}`) || 'null')
            if (!Array.isArray(savedColumnIds)) {
                setCustomizableColumns([...defaultCustomizableColumnsSold])
                return
            }

            const savedColumns = savedColumnIds.flatMap(id => {
                const column = allCustomizableColumnsSold.find(item => item.id === id)
                return column ? [column] : []
            })
            setCustomizableColumns(savedColumns)
        } catch {
            console.error('get sold part list columns failed')
            setCustomizableColumns([...defaultCustomizableColumnsSold])
        }
    }, [userId])

    const saveCustomizableColumns = useCallback(
        (activeColumns: string[]) => {
            setCustomizableColumns(
                activeColumns.flatMap(id => {
                    const column = allCustomizableColumnsSold.find(item => item.id === id)
                    return column ? [column] : []
                }),
            )

            if (userId) {
                localStorage.setItem(`soldPartListColumns:${userId}`, JSON.stringify(activeColumns))
            }
        },
        [userId],
    )

    useEffect(() => {
        setPagesParts(prevPages =>
            prevPages.map(page => ({
                ...page,
                count: counts && counts[page.countKey],
            })),
        )
    }, [counts])

    const loadParts = useCallback(async () => {
        const { partsId } = documents.find(document => document.value === documentId) || {}

        const params: PartListSearchParams = {
            pagination: {
                page,
                perPage,
            },
            order: sortOrder,
            carId: carFilter?.carId,
            modelId,
            markId,
            groupId: categoryId,
            search: partName,
            priceRange,
            partId: partId || partsId,
            partNumber,
            isSold: true,
            dateRange,
            marketplace,
            warehouseId: filterWarehouseId,
        }

        dispatch(loadPartList(params))
    }, [
        carFilter?.carId,
        categoryId,
        dateRange,
        dispatch,
        documentId,
        documents,
        markId,
        marketplace,
        modelId,
        page,
        partId,
        partName,
        partNumber,
        perPage,
        priceRange,
        sortOrder,
        filterWarehouseId,
    ])

    const loadSearchParams = useCallback(async () => {
        const searchParamsObj = Object.fromEntries(searchParams.entries())

        const searchParamCarId = searchParamsObj.carId
        const searchParamPage = searchParamsObj.p
        const searchParamPartName = searchParamsObj.pn
        const searchParamCategoryId = searchParamsObj.g
        const searchParamMarkId = searchParamsObj.markId
        const searchParamModelId = searchParamsObj.modelId
        const searchParamPriceFrom = searchParamsObj.priceFrom
        const searchParamPriceTo = searchParamsObj.priceTo
        const searchParamPartNumber = searchParamsObj.partNumber
        const searchParamSortOrder = searchParamsObj.sortOrder
        const searchParamMarketplace = searchParamsObj.marketplace
        const searchParamDateFrom = searchParamsObj.dateFrom
        const searchParamDateTo = searchParamsObj.dateTo
        const searchParamDocumentId = searchParamsObj.documentId
        const searchParamWarehouseId = searchParamsObj.wh

        if (searchParamPage !== `${page}` && (searchParamPage !== null || page !== 1)) {
            dispatch({
                type: storeDispatchTypes.setPageFilterParts,
                value: { page: Number(searchParamPage) > 1 ? Number(searchParamPage) : 1 },
            })
        }

        if (searchParamPartName !== partName) {
            applyFilters.partName(searchParamPartName)
        }

        if (searchParamCategoryId !== `${categoryId}`) {
            applyFilters.categoryId(searchParamCategoryId)
        }

        if (searchParamMarkId !== `${markId}`) {
            applyFilters.markId(searchParamMarkId)
        }

        if (searchParamModelId !== `${modelId}`) {
            applyFilters.modelId(searchParamModelId)
        }

        if (
            searchParamPriceFrom !== `${priceFrom}` ||
            searchParamPriceFrom === null ||
            searchParamPriceTo !== `${priceTo}` ||
            searchParamPriceTo === null
        ) {
            if (searchParamPriceFrom || priceFrom !== undefined || searchParamPriceTo || priceTo !== undefined) {
                applyFilters.priceRange(searchParamPriceFrom, searchParamPriceTo)
            }
        }

        if (searchParamPartNumber !== `${partNumber}`) {
            applyFilters.partNumber(searchParamPartNumber)
        }

        if (searchParamSortOrder && searchParamSortOrder !== sortOrder.map(pair => pair.join(',')).join(',')) {
            const searchParamsSet = new Set(searchParamSortOrder.split(';'))
            const sortOrderSet = new Set(sortOrder.map(pair => pair.join(',')))

            // Для каждой пары из sortOrder, которая не содержится в searchParamSortOrder
            sortOrder.forEach(pair => {
                const pairString = pair.join(',')
                if (!searchParamsSet.has(pairString)) {
                    applyFilters.sortOrder(pair[0], 'NONE')
                }
            })

            // Для каждой пары из searchParamSortOrder, которая не содержится в sortOrder
            searchParamsSet.forEach(param => {
                if (!sortOrderSet.has(param)) {
                    const [field, order] = param.split(',')
                    applyFilters.sortOrder(field, order)
                }
            })
        } else if (!searchParamSortOrder) {
            sortOrder.forEach(field => applyFilters.sortOrder(field[0], 'NONE'))
        }

        if (searchParamCarId !== `${carFilter?.carId[0]}`) {
            if (!searchParamCarId) {
                dispatch({ type: storeDispatchTypes.resetCarFilterParts })
            } else if (userId) {
                const car = await getCarById(searchParamCarId, userId)
                if (car) {
                    dispatch({
                        type: storeDispatchTypes.setCarFilterParts,
                        value: {
                            car: {
                                carId: [car.id],
                                markTitle: car.markTitle,
                                modelTitle: car.modelTitle,
                                year: car.year,
                            },
                        },
                    })
                }
            }
        } else if (!carFilter?.carId[0]) {
            dispatch({ type: storeDispatchTypes.resetCarFilterParts })
        }

        if (searchParamMarketplace !== String(marketplace)) {
            applyFilters.marketplace(searchParamMarketplace)
        }

        if (
            searchParamDateFrom !== `${dateFrom}` ||
            searchParamDateFrom === null ||
            searchParamDateTo !== `${dateTo}` ||
            searchParamDateTo === null
        ) {
            if (searchParamDateFrom || dateFrom !== undefined || searchParamDateTo || dateTo !== undefined) {
                const fromDate = new Date(Number(searchParamDateFrom))
                const toDate = new Date(Number(searchParamDateTo))

                applyFilters.dateRange(searchParamDateFrom, searchParamDateTo)
                setStartDate(fromDate)
                setEndDate(toDate)
                setPeriod(determinePeriod(fromDate, toDate))
            }
        }

        if (searchParamDocumentId !== documentId) {
            applyFilters.documentId(searchParamDocumentId)
        }

        if (
            searchParamWarehouseId !== filterWarehouseId &&
            (searchParamWarehouseId || filterWarehouseId !== undefined)
        ) {
            filterParts(storeDispatchTypes.setFilterWarehouseIdParts, {
                warehouseId: searchParamWarehouseId || '',
            })
        }

        setIsSearchParamsLoaded(true)
    }, [
        applyFilters,
        carFilter?.carId,
        categoryId,
        dateFrom,
        dateTo,
        dispatch,
        documentId,
        filterWarehouseId,
        markId,
        marketplace,
        modelId,
        page,
        partName,
        partNumber,
        priceFrom,
        priceTo,
        searchParams,
        sortOrder,
        userId,
    ])

    // Сбрасываем флаг, если параметры изменились
    useEffect(() => {
        setIsSearchParamsLoaded(false)
    }, [searchParams])

    // Загрузка параметров из строки поиска
    useEffect(() => {
        if (!isSearchParamsLoaded) {
            loadSearchParams()
        }
    }, [loadSearchParams, isSearchParamsLoaded])

    // Загрузка параметров в строку поиска
    useEffect(() => {
        if (!isSearchParamsLoaded || isLoadingDocuments) return

        const updatedParams = new URLSearchParams(searchParams)

        const paramMap: Record<string, any> = {
            carId: carFilter?.carId?.[0],
            p: page > 1 ? String(page) : undefined,
            pn: partName,
            g: categoryId,
            markId,
            modelId,
            priceFrom,
            priceTo,
            partNumber,
            sortOrder: sortOrder.length ? sortOrder.map(pair => pair.join(',')).join(';') : undefined,
            marketplace,
            documentId,
            dateFrom,
            dateTo,
            wh: filterWarehouseId,
        }

        for (const [key, value] of Object.entries(paramMap)) {
            if (value === undefined || value === '' || value === null) {
                updatedParams.delete(key)
            } else {
                updatedParams.set(key, String(value))
            }
        }

        if (updatedParams.toString() !== searchParams.toString()) {
            setSearchParams(updatedParams)
        } else {
            loadParts()
        }
    }, [
        carFilter?.carId,
        categoryId,
        dateFrom,
        dateTo,
        documentId,
        filterWarehouseId,
        loadParts,
        markId,
        marketplace,
        modelId,
        page,
        partName,
        partNumber,
        priceFrom,
        priceTo,
        isSearchParamsLoaded,
        isLoadingDocuments,
        sortOrder,
    ])

    const handlePeriodChange = useCallback((selectedPeriod: FilterSelect) => {
        setPeriod(selectedPeriod)
        setStartDate(null)
        setEndDate(null)

        const currentDate = new Date()
        let periodStartDate: Date | null = null
        let periodEndDate: Date | null = null

        switch (selectedPeriod) {
            case FilterSelect.YEAR:
                periodStartDate = moment(currentDate).startOf('year').toDate()
                periodEndDate = moment(currentDate).endOf('year').toDate()
                break
            case FilterSelect.QUARTER:
                periodStartDate = moment(currentDate).startOf('quarter').toDate()
                periodEndDate = moment(currentDate).endOf('quarter').toDate()
                break
            case FilterSelect.MONTH:
                periodStartDate = moment(currentDate).startOf('month').toDate()
                periodEndDate = moment(currentDate).endOf('month').toDate()
                break
            case FilterSelect.WEEK:
                periodStartDate = moment(currentDate).startOf('isoWeek').toDate()
                periodEndDate = moment(currentDate).endOf('isoWeek').toDate()
                break
            case FilterSelect.DAY:
                periodStartDate = moment(currentDate).startOf('day').toDate()
                periodEndDate = moment(currentDate).endOf('day').toDate()
                break
        }

        setStartDate(periodStartDate)
        setEndDate(periodEndDate)
    }, [])

    const getPeriodDuration = useCallback((period: FilterSelect): moment.unitOfTime.DurationConstructor => {
        switch (period) {
            case FilterSelect.MONTH:
                return 'month'
            case FilterSelect.QUARTER:
                return 'quarter'
            case FilterSelect.WEEK:
                return 'week'
            case FilterSelect.YEAR:
                return 'year'
            case FilterSelect.DAY:
                return 'day'
            default:
                throw new Error(`Invalid period: ${period}`)
        }
    }, [])

    const handlePreviousPeriod = useCallback(() => {
        const periodDuration = getPeriodDuration(period)
        const previousStartDate = moment(startDate).subtract(1, periodDuration).toDate()
        const previousEndDate = moment(endDate).subtract(1, periodDuration).toDate()

        setStartDate(previousStartDate)
        setEndDate(previousEndDate)
    }, [endDate, getPeriodDuration, period, startDate])

    const handleNextPeriod = useCallback(() => {
        const periodDuration = getPeriodDuration(period)
        const nextStartDate = moment(startDate).add(1, periodDuration).toDate()
        const nextEndDate = moment(endDate).add(1, periodDuration).toDate()

        setStartDate(nextStartDate)
        setEndDate(nextEndDate)
    }, [endDate, getPeriodDuration, period, startDate])

    const determinePeriod = useCallback((start: Date | null, end: Date | null) => {
        if (!start || !end) return FilterSelect.ALL

        const startMoment = moment(start)
        const endMoment = moment(end)

        // Проверка на день
        const isSameDay = startMoment.isSame(endMoment, 'day')
        if (isSameDay) {
            return FilterSelect.DAY
        }

        // Проверка на неделю
        const isSameWeek = startMoment.isSame(endMoment, 'week')
        if (isSameWeek) {
            return FilterSelect.WEEK
        }

        // Проверка на месяц
        const isSameMonth = startMoment.isSame(endMoment, 'month')
        if (
            isSameMonth &&
            startMoment.isSame(startMoment.startOf('month'), 'day') &&
            endMoment.isSame(endMoment.endOf('month'), 'day')
        ) {
            return FilterSelect.MONTH
        }

        // Проверка на квартал
        const isSameQuarter = startMoment.isSame(endMoment, 'quarter')
        if (
            isSameQuarter &&
            startMoment.isSame(startMoment.startOf('quarter'), 'day') &&
            endMoment.isSame(endMoment.endOf('quarter'), 'day')
        ) {
            return FilterSelect.QUARTER
        }

        // Проверка на год
        const isSameYear = startMoment.isSame(endMoment, 'year')
        if (
            isSameYear &&
            startMoment.isSame(startMoment.startOf('year'), 'day') &&
            endMoment.isSame(endMoment.endOf('year'), 'day')
        ) {
            return FilterSelect.YEAR
        }

        return FilterSelect.ALL
    }, [])

    const buttonText = useMemo(
        () =>
            ({
                [FilterSelect.ALL]: null,
                [FilterSelect.YEAR]: moment(startDate).format('YYYY год'),
                [FilterSelect.QUARTER]: moment(startDate).format('Q Квартал YYYY'),
                [FilterSelect.MONTH]: ` Месяц: ${moment(startDate).format('MMMM YYYY')}`,
                [FilterSelect.WEEK]: `Неделя: ${moment(startDate).format(
                    'DD MMM YYYY',
                )}-${moment(endDate).format('DD MMM YYYY')}`,
                [FilterSelect.DAY]: `${moment(startDate).format('DD MMMM')}`,
            })[period],
        [endDate, period, startDate],
    )

    useEffect(() => {
        applyFilters.dateRange(startDate, endDate)
    }, [startDate, endDate, dispatch, applyFilters])

    // Статистика продаж
    const [profit, setProfit] = useState(0)
    const [soldParts, setSoldParts] = useState(0)
    const [averageCheck, setAverageCheck] = useState(0)
    const userSummary = useSelector(state => state.sales.userSummary)
    const isSummaryLoading = useSelector(state => state.sales.loaders.userSummary)

    useEffect(() => {
        setProfit(userSummary.soldSum)
        setSoldParts(userSummary.soldCount)
        setAverageCheck(userSummary.soldCount ? Math.round(userSummary.soldSum / userSummary.soldCount) : 0)
    }, [userSummary])

    useEffect(() => {
        if (!isSearchParamsLoaded || isLoadingDocuments) return
        const { partsId } = documents.find(document => document.value === documentId) || {}
        dispatch(getSummaryByUserId(dateRange, marketplace, partsId, filterWarehouseId))
    }, [dateRange, dispatch, documentId, documents, filterWarehouseId, isLoadingDocuments, isSearchParamsLoaded, marketplace])

    // Главный поиск
    const setNameSearch = useCallback(
        (searchTerm: string) => {
            dispatch({
                type: storeDispatchTypes.setPartNameTextFilterParts,
                value: {
                    partName: searchTerm,
                },
            })
        },
        [dispatch],
    )

    const setCurrentPart = useCallback(
        (currentPart?: Part) => {
            dispatch({
                type: storeDispatchTypes.setCurrentPart,
                value: currentPart,
            })
        },
        [dispatch],
    )

    const navigateWithParams = useCallback(
        (partId = '') => {
            const queryParams = new URLSearchParams(location.search)

            if (partId) {
                if (params.partId !== partId) {
                    navigate(`/admin/parts/sold/${partId}?${queryParams.toString()}`)
                }
            } else {
                navigate(`/admin/parts/sold?${queryParams.toString()}`)
            }
        },
        [navigate, params.partId],
    )

    const openPartModal = useCallback(
        (part: Part) => {
            navigateWithParams(part.id)
            setIsPartModalOpen(true)
            setCurrentPart(part)
        },
        [navigateWithParams, setCurrentPart],
    )

    const handleClose = useCallback(() => {
        navigateWithParams()
        setIsPartModalOpen(false)
    }, [navigateWithParams])

    const getPartById = useCallback(
        async (partId: string) => {
            const part = await DatahubApiService.getPartByPartId(partId, false, true)

            if (!part) {
                return
            } else {
                setCurrentPart(part)
                setIsPartModalOpen(true)
            }
        },
        [setCurrentPart],
    )

    // Промежуточная модалка-подтверждение
    const [isApplyActionModalOpen, setIsApplyActionModalOpen] = useState(false)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
    const [pendingAction, setPendingAction] = useState<null | ((parts?: Part[]) => void)>(null)
    const [isDisableFilterButton, setIsDisableFilterButton] = useState(false)
    const [modalParts, setModalParts] = useState<Part[]>([])

    const selectAll = useSelector(state => state.partList.selectedParts.selectAll)
    const selectedParts = useSelector(state => state.partList.selectedParts.parts)

    const handleActionWithScopeSelection = (action: (parts?: Part[]) => void, disableFilterButton: boolean = false) => {
        setPendingAction(() => action)

        if (!selectAll) {
            setModalParts(selectedParts)
            setIsConfirmModalOpen(true)
            return
        }

        setIsDisableFilterButton(disableFilterButton)
        setIsApplyActionModalOpen(true)
    }

    const handleApplyForPage = () => {
        setModalParts(parts)
        setIsApplyActionModalOpen(false)
        setIsConfirmModalOpen(true)
    }

    const handleApplyForFilter = () => {
        setModalParts([])
        setIsApplyActionModalOpen(false)
        setIsConfirmModalOpen(true)
    }

    const handleConfirmAction = () => {
        if (pendingAction) pendingAction(modalParts)
        setIsConfirmModalOpen(false)
    }

    const handleCheckboxChange = (part: Part) => {
        const newCheckedParts = [...selectedParts]
        const partIndex = newCheckedParts.findIndex(p => p.id === part.id)

        if (partIndex !== -1) {
            newCheckedParts.splice(partIndex, 1)
        } else {
            newCheckedParts.push(part)
        }

        dispatch({
            type: storeDispatchTypes.setSelectedParts,
            value: newCheckedParts,
        })
    }

    const handleAllCheckboxCancel = () => {
        setModalParts([])
        dispatch({
            type: storeDispatchTypes.setSelectAllParts,
            value: false,
        })
    }

    const handleAllCheckboxChange = () => {
        const newSelectAll = !selectAll
        dispatch({
            type: storeDispatchTypes.setSelectAllParts,
            value: newSelectAll,
        })
    }

    const handleUnsetPartsSoldStatus = async (parts?: Part[]) => {
        const partsId = parts?.map(part => part.id)

        if (partsId) {
            try {
                const promises = partsId.map(partId => dispatch(setPartSoldStatus(partId, false)))

                await Promise.all(promises)

                toast.success('Запчасти успешно возвращены из проданного')

                loadParts()
            } catch (error) {
                toast.error('Ошибка возврата из проданного')
            }
        }

        handleAllCheckboxCancel()
    }

    // open modal by partId
    useEffect(() => {
        if (params.partId) {
            getPartById(params.partId)
        } else {
            setIsPartModalOpen(false)
        }
    }, [getPartById, params.partId])

    // Сброс фильтров при уходе со страницы
    useResetFiltersOnUnmount()

    return (
        <div className={styles.wrap}>
            <ToastContainer position="bottom-center" autoClose={5000} />
            {/* Модалки */}
            {isApplyActionModalOpen && (
                <ModalPartsBulkApply
                    isOpen={isApplyActionModalOpen}
                    onClose={() => setIsApplyActionModalOpen(false)}
                    onApplyForPage={handleApplyForPage}
                    onApplyForFilter={handleApplyForFilter}
                    disableFilterButton={isDisableFilterButton}
                />
            )}
            {isConfirmModalOpen && (
                <ModalPartsBulkConfirm
                    isOpen={isConfirmModalOpen}
                    onClose={() => {
                        handleAllCheckboxCancel()
                        setIsConfirmModalOpen(false)
                    }}
                    onConfirm={handleConfirmAction}
                    partsCount={selectAll ? modalParts.length || itemsCount : selectedParts.length}
                />
            )}
            {isCustomizableColumnsModalOpen && (
                <ModalCustomizableColumns
                    isOpen={isCustomizableColumnsModalOpen}
                    closeModal={() => setIsCustomizableColumnsModalOpen(false)}
                    customizableColumns={customizableColumns}
                    availableColumns={allCustomizableColumnsSold}
                    onSave={saveCustomizableColumns}
                />
            )}

            {currentPart && isPartModalOpen && (
                <ModalViewPart part={currentPart} isOpen={isPartModalOpen} closeModal={() => handleClose()} />
            )}
            <div className={styles.rowWrap}>
                <SalesText
                    text={'Проданные запчасти'}
                    fontWeight={fontWeights.bold}
                    fontSize={fontSizes.xxl}
                    letterSpacing={-0.02}
                />
            </div>
            <div className={styles.rowWrap}>
                <SearchComponent oldSearchTerm={partName} setSearchTerm={setNameSearch} />
            </div>
            <div className={styles.rowWrap}>
                <Dropdown className={styles.customDropdown}>
                    <Dropdown.Toggle disabled={!parts.length || (selectedParts.length == 0 && !selectAll)}>
                        Массовые действия
                        {!!(selectAll || selectedParts.length) && (
                            <span style={{ marginLeft: '8px' }}>{selectAll ? itemsCount : selectedParts.length}</span>
                        )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            onClick={() => {
                                handleActionWithScopeSelection(handleUnsetPartsSoldStatus, true)
                            }}
                        >
                            Вернуть из проданного
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
                <NavigationButtons
                    pages={pagesParts}
                    currentPage={getAdminNavigationPath(adminRouteAlias.soldParts.location)}
                    counts={counts}
                    navigate={navigate}
                />
                {warehousesSS.length > 0 && (
                    <Dropdown className={classNames(styles.filterDropdown, 'ms-auto')}>
                        <Dropdown.Toggle>
                            {filterWarehouseId === 'null'
                                ? 'Без склада'
                                : filterWarehouseId
                                  ? warehousesSS.find(w => String(w.value) === filterWarehouseId)?.name ||
                                    filterWarehouseId
                                  : 'Выбрать склад'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => filterPartsByWarehouseId('')}>Все</Dropdown.Item>
                            <Dropdown.Item onClick={() => filterPartsByWarehouseId('null')}>Без склада</Dropdown.Item>
                            {warehousesSS.map(w => (
                                <Dropdown.Item
                                    key={String(w.value)}
                                    onClick={() => filterPartsByWarehouseId(String(w.value))}
                                >
                                    {w.name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                <Dropdown className={classNames(styles.customDropdown, warehousesSS.length > 0 ? undefined : 'ms-auto')}>
                    <Dropdown.Toggle>{marketplace ? Marketplaces[marketplace] : 'Выбрать маркетплейс'}</Dropdown.Toggle>

                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => filterPartsByMarketplace(null)}>Все</Dropdown.Item>
                        {Object.entries(Marketplaces).map(([key, value]) => (
                            <Dropdown.Item key={key} onClick={() => filterPartsByMarketplace(key as MarketplacesKeys)}>
                                {value}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <SalesButton
                    borderRadius={10}
                    hoverBackgroundColor={colors.grayLighter}
                    paddingHorizontalPixels={20}
                    paddingVerticalPixels={7}
                    onClick={() => setIsCustomizableColumnsModalOpen(true)}
                >
                    <img src={gear} alt="Настроить столбцы" className={styles.icon} />
                </SalesButton>
            </div>
            <div className={styles.sortControls}>
                {Object.entries(FilterSelect).map(([key, value]) => (
                    <SalesButton
                        key={key}
                        borderRadius={10}
                        backgroundColor={value === period ? colors.grayDark : undefined}
                        paddingHorizontalPixels={12}
                        paddingVerticalPixels={7}
                        onClick={() => handlePeriodChange(value)}
                    >
                        <SalesText
                            text={value}
                            fontSize={fontSizes.s}
                            color={value === period ? colors.white : undefined}
                        />
                    </SalesButton>
                ))}

                {carFilter && (
                    <Link
                        to={`${getAdminNavigationPath(adminRouteAlias.cars.location)}/${carFilter?.carId}`}
                        style={{ textDecoration: 'none' }}
                    >
                        <SalesButton borderRadius={10} paddingHorizontalPixels={20} paddingVerticalPixels={7}>
                            <SalesText
                                text={`Перейти к авто ${carFilter?.markTitle} ${carFilter?.modelTitle} ${carFilter?.year}`}
                                fontSize={fontSizes.s}
                            />
                        </SalesButton>
                    </Link>
                )}

                <div className={'ms-auto'}>
                    <Form.Select value={documentId ? documentId : ''} onChange={handleSelectChangeDocument}>
                        <option value={''}>Выбрать накладную</option>
                        {documents.map(document => (
                            <option key={document.value} value={document.value}>
                                {document.name}
                            </option>
                        ))}
                    </Form.Select>
                </div>
            </div>
            {buttonText && (
                <div className={styles.sortControls}>
                    <div className={styles.sortControls}>
                        <SalesButton
                            borderRadius={10}
                            backgroundColor={colors.grayDark}
                            paddingHorizontalPixels={12}
                            paddingVerticalPixels={7}
                            onClick={handlePreviousPeriod}
                        >
                            <ChevronLeft size={18} color={colors.white} />
                        </SalesButton>
                        <SalesButton
                            borderRadius={10}
                            backgroundColor={colors.grayDark}
                            paddingHorizontalPixels={12}
                            paddingVerticalPixels={7}
                            onClick={() => handlePeriodChange(period)}
                        >
                            <SalesText text={buttonText} fontSize={fontSizes.s} color={colors.white} />
                        </SalesButton>
                        <SalesButton
                            borderRadius={10}
                            backgroundColor={colors.grayDark}
                            paddingHorizontalPixels={12}
                            paddingVerticalPixels={7}
                            onClick={handleNextPeriod}
                            disabled={moment(endDate).isSameOrAfter(new Date(), 'day')}
                        >
                            <ChevronRight size={18} color={colors.white} />
                        </SalesButton>
                    </div>
                </div>
            )}
            <div className={styles.statiscticsBlock}>
                {!!parts.length && canShowFinanceInfo && !isSummaryLoading && (
                    <>
                        <p className={styles.statiscticsBlockText}>
                            Выручка за период: <b>{profit.toLocaleString('ru-RU')}</b>{' '}
                            {getCurrencySymbol(currentCurrency)}
                        </p>
                        <p className={styles.statiscticsBlockText}>
                            Продано запчастей: <b>{soldParts.toLocaleString('ru-RU')}</b>
                        </p>
                        <p className={styles.statiscticsBlockText}>
                            Средний чек: <b>{averageCheck.toLocaleString('ru-RU')}</b>{' '}
                            {getCurrencySymbol(currentCurrency)}
                        </p>
                    </>
                )}
                {!isLoading && parts.length === 0 ? (
                    <></>
                ) : (
                    <div className={styles.wrapRight}>
                        <ListPagination
                            page={page}
                            setPage={page =>
                                dispatch({
                                    type: storeDispatchTypes.setPageFilterParts,
                                    value: { page },
                                })
                            }
                            perPage={perPage}
                            itemsCount={itemsCount}
                        />
                    </div>
                )}
            </div>

            <TableSoldPartList
                columns={customizableColumns}
                parts={parts}
                isLoading={isLoading}
                selectAll={selectAll}
                selectedParts={selectedParts}
                partName={partName}
                filterVendorCode={filterVendorCode}
                carFilter={carFilter}
                priceFrom={priceFrom}
                priceTo={priceTo}
                handleAllCheckboxChange={handleAllCheckboxChange}
                handleCheckboxChange={handleCheckboxChange}
                openPartModal={openPartModal}
            />
            {!isLoading && parts.length === 0 ? (
                <ListNoItems slice={'partList'} />
            ) : (
                <ListPagination
                    page={page}
                    setPage={page =>
                        dispatch({
                            type: storeDispatchTypes.setPageFilterParts,
                            value: { page },
                        })
                    }
                    perPage={perPage}
                    itemsCount={itemsCount}
                    justifyContent={'flex-end'}
                />
            )}
        </div>
    )
}

export default SoldPartList
